import {
  useWSModalCustom,
  WSAlert,
  WSElement,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSectionToolbar,
  WSSelect,
  WSSelectOption,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayeeEngagementStatus,
  PayeeEngagementType,
  WorkLog,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import {
  queriesEngagements,
  queriesPayeeEngagements,
  queriesPayPeriods,
  queriesWorkLogs
} from "@query";

import { ErrorAlert } from "@components/ErrorAlert";
import { DataView } from "../../../../shared/components/DataView";
import { SelectPayee } from "../../../Payees/components/SelectPayee";
import { useCurrentPayPeriod } from "../../hooks/useCurrentPayPeriod";
import { routeHelpersWorkLogsPayer } from "../../paths";
import { selectorPayPeriodOptions } from "../../selectors/selectorPayPeriodOptions";
import { selectorWorkLogPayeeEngagementOptions } from "../../selectors/selectorWorkLogPayeeEngagementOptions";

/**
 * An assignment the payer can still create work against. The API refuses an
 * inactive engagement outright ("Cannot create work log for an inactive
 * engagement"), so offering one would only produce a server error — the same
 * pair `SelectEngagementForPayee` treats as live.
 */
const SELECTABLE_STATUSES: PayeeEngagementStatus[] = [
  PayeeEngagementStatus.CREATED,
  PayeeEngagementStatus.ACTIVATED
];

/**
 * Work logs still in play for an assignment. An approved or rejected one is
 * settled and says nothing about whether a new log duplicates work in progress.
 */
const OPEN_WORK_LOG_STATUSES: WorkLogStatus[] = [
  WorkLogStatus.DRAFT,
  WorkLogStatus.IN_PROGRESS,
  WorkLogStatus.IN_REVIEW
];

type FormData = {
  payeeId: string;
  payeeEngagementId: string;
  /**
   * Mirrored into form state purely so Yup can make the pay period conditionally
   * required — `WSForm` takes one schema up front, so the condition has to be
   * expressed against a field rather than a closure.
   */
  engagementType: string;
  payPeriodId: string;
};

type Props = WSModalCustomProps<WorkLog | undefined>;

/**
 * Payer-side "New work log".
 *
 * The contractor and the assignment are picked separately, from different
 * sources on purpose. The contractor picker is the shared search-backed
 * `SelectPayee`, so it paginates and searches server-side and labels people with
 * the same resolved name every other list shows. The assignment options come
 * from the first-class collections (`listPayeeEngagements` + `listEngagements`)
 * and derive the work-logging rule from `Engagement.workDefinitionIds` — the
 * search row's `isWorkLoggingEnabled` is only a projection of that, and reading
 * the source keeps the rule correct even when the index lags.
 *
 * The contractor list is deliberately the payer's whole roster: narrowing it to
 * contractors who happen to have a work-logging assignment would silently hide
 * people, so the rule is applied to the *assignment* options, where a contractor
 * with none can be told why.
 *
 * The pay period is asked for — and required — only on W-2 `Employee`
 * assignments: it is what ties the work log to a payroll run. A contractor work
 * log has no pay period at all (the API ignores `payPeriodId` for those), so the
 * field is not offered rather than pretending to set something.
 *
 * A payer-created work log opens **in review**: the payer is both author and
 * approver, so it skips the payee's draft/submit steps and lands in their own
 * "To approve" queue — which is also the only status where work items can be
 * edited (`canPayerEdit`).
 *
 * Resolves with the created `WorkLog` so the caller decides where to go next.
 */
const ModalCreateWorkLogPayer: React.FC<Props> = props => {
  const history = useHistory();

  // Mirrors the form's contractor field so the assignment query can run at the
  // top level, where hooks belong.
  const [payeeId, setPayeeId] = useState<string | null>(null);
  const [payeeEngagementId, setPayeeEngagementId] = useState<string | null>(
    null
  );

  const queryPayeeEngagements =
    queriesPayeeEngagements.listPayeeEngagements.useQueryAll(
      { payeeId: payeeId as string },
      { enabled: !!payeeId }
    );

  // The payer's engagement templates carry the work definitions the rule keys
  // on. A small, account-wide list, so it is loaded once and reused.
  const queryEngagements = queriesEngagements.listEngagements.useQueryAll();

  const engagements = useMemo(
    () =>
      selectorWorkLogPayeeEngagementOptions({
        payeeEngagements: queryPayeeEngagements.flatData ?? [],
        engagements: queryEngagements.flatData ?? []
      }).filter(
        engagement =>
          !!engagement.status && SELECTABLE_STATUSES.includes(engagement.status)
      ),
    [queryPayeeEngagements.flatData, queryEngagements.flatData]
  );

  const engagementOptions = useMemo<WSSelectOption[]>(
    () =>
      engagements.map(engagement => ({
        value: engagement.value,
        label: engagement.label
      })),
    [engagements]
  );

  const isEngagementsLoading =
    queryPayeeEngagements.isLoading || queryEngagements.isLoading;

  const hasNoWorkLoggingAssignments =
    !!payeeId && !isEngagementsLoading && engagements.length === 0;

  const queryPayPeriods = queriesPayPeriods.listPayPeriods.useQueryAll();

  const payPeriodOptions = useMemo(
    () => selectorPayPeriodOptions(queryPayPeriods.flatData ?? []),
    [queryPayPeriods.flatData]
  );

  const { currentPayPeriod } = useCurrentPayPeriod();

  // Work already open on this assignment, so the payer can continue it instead
  // of starting a duplicate.
  const queryExistingWorkLogs = queriesWorkLogs.listWorkLogs.usePaginatedQuery(
    {
      filter: {
        payeeEngagementId: { eq: payeeEngagementId as string },
        status: { anyOf: OPEN_WORK_LOG_STATUSES }
      }
    },
    { enabled: !!payeeEngagementId }
  );

  const existingWorkLogs = queryExistingWorkLogs.flatData ?? [];

  const [createWorkLog, createWorkLogMeta] =
    queriesWorkLogs.createWorkLog.useMutation();

  const handleViewExistingWorkLogs = () => {
    history.push(
      routeHelpersWorkLogsPayer.createLocationObject({
        search: {
          filter: { payeeEngagementId: payeeEngagementId ?? undefined }
        }
      })
    );
    props.onClose(undefined);
  };

  const handleSubmit = (formData: FormData) => {
    const isEmployee = formData.engagementType === PayeeEngagementType.EMPLOYEE;

    createWorkLog(
      {
        workLogCreate: {
          payeeEngagementId: formData.payeeEngagementId,
          // Sent only where the API reads it — a contractor work log has no pay
          // period, and the API ignores the field for those.
          ...(isEmployee && formData.payPeriodId
            ? { payPeriodId: formData.payPeriodId }
            : {})
        }
      },
      { onSuccess: workLog => props.onClose(workLog) }
    );
  };

  return (
    <WSForm<FormData>
      defaultValues={{
        payeeId: "",
        payeeEngagementId: "",
        engagementType: "",
        payPeriodId: currentPayPeriod?.id ?? ""
      }}
      validationSchema={Yup.object().shape({
        payeeId: Yup.string().required("Contractor is required"),
        payeeEngagementId: Yup.string().required("Assignment is required"),
        engagementType: Yup.string(),
        // Only a W-2 work log has a pay period, so only there is it required.
        payPeriodId: Yup.string().when("engagementType", {
          is: PayeeEngagementType.EMPLOYEE,
          then: Yup.string().required("Pay period is required"),
          otherwise: Yup.string()
        })
      })}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: submitForm, setValue, watch }) => {
        const engagement = engagements.find(
          option => option.value === watch("payeeEngagementId")
        );
        const isEmployee =
          engagement?.engagementType === PayeeEngagementType.EMPLOYEE;

        return (
          <WSModal
            actions={{
              alignment: "fill",
              buttons: [
                {
                  kind: "Tertiary",
                  label: "Cancel",
                  type: "button",
                  onClick: () => props.onClose(undefined)
                },
                {
                  kind: "Primary",
                  label: "Create work log",
                  loading: createWorkLogMeta.isLoading,
                  // Submitted by calling the form handler rather than by button
                  // type: `WSModal` renders its content in a portal, so a native
                  // submit button in the footer never reaches the <form> element
                  // `WSForm` creates. React context still crosses the portal, so
                  // the fields and validation work either way — only the DOM
                  // submit event does not.
                  type: "button",
                  onClick: submitForm(handleSubmit)
                }
              ]
            }}
            divider
            title="New work log"
            onClose={() => props.onClose(undefined)}
          >
            <WSList gap="L">
              <WSText.ParagraphSm color="gray500">
                Create a work log on a contractor&apos;s behalf. It opens in
                review in your To approve tab, where you can add work items
                before approving it.
              </WSText.ParagraphSm>

              <WSElement>
                <WSSectionToolbar mb="XS" title="Contractor assignment *" />
                <WSText.ParagraphSm color="gray500" mb="M">
                  Select the contractor and the assignment to log work against
                </WSText.ParagraphSm>

                <WSList gap="M">
                  <WSForm.Field
                    component={SelectPayee}
                    componentProps={{ placeholder: "Select a contractor" }}
                    label="Contractor"
                    name="payeeId"
                    onChange={(newPayeeId: string | null) => {
                      setPayeeId(newPayeeId);
                      // An assignment only exists in the context of its
                      // contractor, so keeping the old one would leave a
                      // mismatched pair.
                      setPayeeEngagementId(null);
                      setValue("payeeEngagementId", "");
                      setValue("engagementType", "");
                    }}
                  />

                  <WSForm.Field
                    component={WSSelect}
                    componentProps={{
                      disabled: !payeeId,
                      emptyState: {
                        type: "documents",
                        title: "No work-logging assignments",
                        description:
                          "An assignment needs work definitions attached before it can carry a work log."
                      },
                      internalSearch: true,
                      isListLoading: isEngagementsLoading,
                      options: engagementOptions,
                      placeholder: payeeId
                        ? "Select an assignment"
                        : "Select a contractor first"
                    }}
                    label="Assignment"
                    name="payeeEngagementId"
                    onChange={(newValue: string | null) => {
                      setPayeeEngagementId(newValue ?? null);
                      setValue(
                        "engagementType",
                        engagements.find(option => option.value === newValue)
                          ?.engagementType ?? ""
                      );
                    }}
                  />
                </WSList>
              </WSElement>

              {hasNoWorkLoggingAssignments && (
                <WSAlert
                  icon="info-circle"
                  theme="info"
                  title="No work-logging assignments"
                >
                  None of this contractor&apos;s assignments have work
                  definitions attached, so none can carry a work log. Add one to
                  the engagement, or pick a different contractor.
                </WSAlert>
              )}

              {isEmployee && (
                <WSForm.Field
                  component={WSSelect}
                  componentProps={{
                    internalSearch: true,
                    isListLoading: queryPayPeriods.isLoading,
                    options: payPeriodOptions,
                    placeholder: "Select a pay period"
                  }}
                  label="Pay period *"
                  name="payPeriodId"
                />
              )}

              {engagement && (
                <DataView
                  data={[
                    { label: "Assignment", value: engagement.label },
                    {
                      label: "Engagement type",
                      value: isEmployee ? "Employee (W-2)" : "Contractor"
                    }
                  ]}
                />
              )}

              {engagement && existingWorkLogs.length > 0 && (
                <WSAlert
                  action={{
                    text: "View existing work logs",
                    onClick: handleViewExistingWorkLogs
                  }}
                  icon="info-circle"
                  theme="info"
                  title="Existing work logs found"
                >
                  This assignment already has {existingWorkLogs.length} open
                  work log{existingWorkLogs.length > 1 ? "s" : ""}. Continue one
                  of those, or create a fresh work log.
                </WSAlert>
              )}

              {engagement && !isEmployee && (
                <WSAlert icon="info-circle" theme="info" title="No pay period">
                  Contractor work logs are not tied to a pay period, so this one
                  will not join a payroll run.
                </WSAlert>
              )}

              <ErrorAlert context="WorkLog" error={createWorkLogMeta.error} />
            </WSList>
          </WSModal>
        );
      }}
    </WSForm>
  );
};

export const useModalCreateWorkLogPayer = () =>
  useWSModalCustom(ModalCreateWorkLogPayer);
